import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Sparkles, CreditCard, Lock, Check, Loader2, ShoppingCart } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { ModuleSuccess } from '../../ui/ModuleSuccess';

export const StripeCheckout = () => {
    const { completeModule } = useAcademyStore();
    const [completed, setCompleted] = useState(false);

    const [cardNumber, setCardNumber] = useState('');
    const [expiry, setExpiry] = useState('');
    const [cvc, setCvc] = useState('');
    const [processing, setProcessing] = useState(false);
    const [success, setSuccess] = useState(false);
    const [showModuleSuccess, setShowModuleSuccess] = useState(false);
    const [error, setError] = useState('');

    const formatCardNumber = (value: string) => {
        const v = value.replace(/\D/g, '').slice(0, 16);
        return v.replace(/(.{4})/g, '$1 ').trim();
    };

    const formatExpiry = (value: string) => {
        const v = value.replace(/\D/g, '').slice(0, 4);
        if (v.length >= 2) {
            return v.slice(0, 2) + '/' + v.slice(2);
        }
        return v;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        // Validate
        const cardClean = cardNumber.replace(/\s/g, '');
        if (cardClean.length !== 16) {
            setError('Card number must be 16 digits');
            return;
        }
        if (expiry.length !== 5) {
            setError('Invalid expiry date');
            return;
        }
        if (cvc.length < 3) {
            setError('CVC must be 3-4 digits');
            return;
        }

        // Simulate processing
        setProcessing(true);
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Check for test card
        if (cardClean === '4242424242424242') {
            setSuccess(true);
            if (!completed) {
                setCompleted(true);
                completeModule('stripe-integration', 150, 'badge_systems');
                setShowModuleSuccess(true);
            }
        } else if (cardClean === '4000000000000002') {
            setError('Card declined. Try the test card: 4242 4242 4242 4242');
        } else {
            setError('Use test card: 4242 4242 4242 4242');
        }

        setProcessing(false);
    };

    const reset = () => {
        setCardNumber('');
        setExpiry('');
        setCvc('');
        setSuccess(false);
        setError('');
    };

    return (
        <div className="min-h-screen bg-slate-900 font-sans text-slate-100 pt-24">

            <div className="max-w-4xl mx-auto p-8">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-black font-comic text-white mb-2 flex items-center justify-center gap-3">
                        <CreditCard className="text-cyan-500" /> Stripe Checkout Flow
                    </h1>
                    <p className="text-slate-400 max-w-2xl mx-auto">
                        Learn how payment forms work. This simulates a Stripe checkout experience.
                        <br />
                        <span className="text-cyan-400">Test Card:</span> <code className="bg-slate-800 px-2 py-0.5 rounded">4242 4242 4242 4242</code>
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Order Summary */}
                    <div className="bg-slate-800 rounded-3xl p-6 border border-slate-700">
                        <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                            <ShoppingCart size={20} className="text-slate-500" /> Order Summary
                        </h2>

                        <div className="space-y-4 mb-6">
                            <div className="flex items-center gap-4 bg-slate-900 rounded-xl p-4">
                                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-2xl">
                                    🦍
                                </div>
                                <div className="flex-1">
                                    <div className="font-bold text-white">Gorilla Scouts Pro</div>
                                    <div className="text-sm text-slate-400">Lifetime Access</div>
                                </div>
                                <div className="text-lg font-bold text-white">$99</div>
                            </div>
                        </div>

                        <div className="border-t border-slate-700 pt-4 space-y-2">
                            <div className="flex justify-between text-sm text-slate-400">
                                <span>Subtotal</span>
                                <span>$99.00</span>
                            </div>
                            <div className="flex justify-between text-sm text-slate-400">
                                <span>Tax</span>
                                <span>$0.00</span>
                            </div>
                            <div className="flex justify-between text-lg font-bold text-white pt-2 border-t border-slate-700">
                                <span>Total</span>
                                <span>$99.00</span>
                            </div>
                        </div>
                    </div>

                    {/* Payment Form */}
                    <div className="bg-white rounded-3xl p-6 shadow-2xl">
                        <AnimatePresence mode="wait">
                            {success ? (
                                <motion.div
                                    key="success"
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="text-center py-12"
                                >
                                    <motion.div
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        transition={{ type: 'spring', stiffness: 200 }}
                                        className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6"
                                    >
                                        <Check size={40} className="text-white" />
                                    </motion.div>
                                    <h3 className="text-2xl font-bold text-green-600 mb-2">Payment Successful!</h3>
                                    <p className="text-slate-500 mb-6">Thank you for your purchase.</p>
                                    <button
                                        onClick={reset}
                                        className="text-blue-600 font-bold hover:underline"
                                    >
                                        Make Another Payment
                                    </button>
                                </motion.div>
                            ) : (
                                <motion.form
                                    key="form"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    onSubmit={handleSubmit}
                                    className="space-y-4"
                                >
                                    <div className="flex items-center gap-2 mb-4">
                                        <Lock size={16} className="text-slate-400" />
                                        <span className="text-sm text-slate-500">Secure Payment</span>
                                        <img src="https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg" alt="Stripe" className="h-6 ml-auto opacity-50" />
                                    </div>

                                    <div>
                                        <label className="text-sm font-bold text-slate-600 mb-1 block">Card Number</label>
                                        <div className="relative">
                                            <input
                                                type="text"
                                                value={cardNumber}
                                                onChange={e => setCardNumber(formatCardNumber(e.target.value))}
                                                placeholder="4242 4242 4242 4242"
                                                className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-slate-800 focus:border-blue-500 outline-none font-mono"
                                            />
                                            <CreditCard className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300" size={20} />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm font-bold text-slate-600 mb-1 block">Expiry</label>
                                            <input
                                                type="text"
                                                value={expiry}
                                                onChange={e => setExpiry(formatExpiry(e.target.value))}
                                                placeholder="MM/YY"
                                                className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-slate-800 focus:border-blue-500 outline-none font-mono"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-sm font-bold text-slate-600 mb-1 block">CVC</label>
                                            <input
                                                type="text"
                                                value={cvc}
                                                onChange={e => setCvc(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                                placeholder="123"
                                                className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-slate-800 focus:border-blue-500 outline-none font-mono"
                                            />
                                        </div>
                                    </div>

                                    {error && (
                                        <div className="bg-red-50 text-red-600 text-sm p-3 rounded-xl border border-red-200">
                                            {error}
                                        </div>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={processing}
                                        className={`w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2 transition-all ${processing
                                            ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                                            : 'bg-blue-600 hover:bg-blue-500 text-white'
                                            }`}
                                    >
                                        {processing ? (
                                            <>
                                                <Loader2 className="animate-spin" size={20} />
                                                Processing...
                                            </>
                                        ) : (
                                            <>Pay $99.00</>
                                        )}
                                    </button>
                                </motion.form>
                            )}
                        </AnimatePresence>
                    </div>
                </div>

                {/* Code Example */}
                <div className="mt-8 bg-slate-800 rounded-2xl p-6 border border-slate-700">
                    <h3 className="font-bold text-white mb-3">Backend Integration</h3>
                    <div className="bg-slate-900 rounded-xl p-4 font-mono text-sm text-green-400 overflow-x-auto">
                        <div className="text-slate-500">// Node.js + Stripe</div>
                        <div>const stripe = require('stripe')('sk_test_...');</div>
                        <div className="mt-2">const paymentIntent = await stripe.paymentIntents.create({'{'}</div>
                        <div className="pl-4">amount: <span className="text-yellow-400">9900</span>,</div>
                        <div className="pl-4">currency: <span className="text-cyan-400">'usd'</span>,</div>
                        <div className="pl-4">payment_method: <span className="text-cyan-400">'pm_card_visa'</span>,</div>
                        <div>{'}'});</div>
                    </div>
                </div>
            </div>
            {showModuleSuccess && (
                <ModuleSuccess
                    title="Payment Integration"
                    badgeId="badge_systems"
                    onRetry={() => setShowModuleSuccess(false)}
                >
                    <div className="bg-slate-800 rounded-2xl p-4 text-left mx-auto max-w-sm border border-slate-700">
                        <div className="font-bold text-cyan-400 mb-2">💳 Transaction Complete:</div>
                        <ul className="text-slate-300 text-sm space-y-1">
                            <li>• Securely tokenized card data</li>
                            <li>• Communicated with Stripe API</li>
                            <li>• Verified transaction status</li>
                        </ul>
                    </div>
                </ModuleSuccess>
            )}
        </div>
    );
};
